import React from 'react';
import { Button } from './Button';

export interface LoadingStateProps {
  /** Loading message to display */
  message?: string;
  /** Whether to show progress indicator */
  showProgress?: boolean;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Whether to show cancel button */
  showCancel?: boolean;
  /** Cancel button handler */
  onCancel?: () => void;
  /** Cancel button text */
  cancelText?: string;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * LoadingState component for displaying loading states during API calls
 * Supports progress indication and cancellation functionality
 * Requirements: 5.1, 5.2
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'プランを検索中...',
  showProgress = true,
  progress = 0,
  showCancel = true,
  onCancel,
  cancelText = 'キャンセル',
  className = '',
  'data-testid': testId = 'loading-state'
}) => {
  return (
    <div 
      className={`flex flex-col items-center justify-center p-8 ${className}`}
      data-testid={testId}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      {/* Loading spinner */}
      <div className="relative mb-4">
        <div 
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
          aria-hidden="true"
        />
        {showProgress && progress > 0 && (
          <div 
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span className="text-xs font-medium text-primary-600">
              {Math.round(progress)}%
            </span>
          </div>
        )}
      </div>

      {/* Loading message */}
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {message}
      </h3>

      {/* Progress bar */}
      {showProgress && (
        <div className="w-full max-w-xs mb-4">
          <div 
            className="bg-gray-200 rounded-full h-2"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`進行状況: ${Math.round(progress)}%`}
          >
            <div 
              className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0%</span>
            <span className="font-medium">{Math.round(progress)}%</span>
            <span>100%</span>
          </div>
        </div>
      )}

      {/* Loading steps indicator */}
      <div className="text-sm text-gray-600 text-center mb-6 max-w-md">
        <p>外部APIからデータを取得しています...</p>
        <ul className="mt-2 space-y-1 text-xs">
          <li className="flex items-center justify-center">
            <span className="inline-block w-2 h-2 bg-primary-600 rounded-full mr-2 animate-pulse" />
            飲食店・カフェ情報
          </li>
          <li className="flex items-center justify-center">
            <span className="inline-block w-2 h-2 bg-primary-600 rounded-full mr-2 animate-pulse" />
            書店・美術館情報
          </li>
          <li className="flex items-center justify-center">
            <span className="inline-block w-2 h-2 bg-primary-600 rounded-full mr-2 animate-pulse" />
            公園・施設情報
          </li>
          <li className="flex items-center justify-center">
            <span className="inline-block w-2 h-2 bg-primary-600 rounded-full mr-2 animate-pulse" />
            天気情報
          </li>
        </ul>
      </div>

      {/* Cancel button */}
      {showCancel && onCancel && (
        <Button
          onClick={onCancel}
          variant="secondary"
          className="min-w-[100px] px-3 py-2 text-sm"
          data-testid="cancel-button"
          aria-label="プラン検索をキャンセル"
        >
          {cancelText}
        </Button>
      )}

      {/* Screen reader only text for better accessibility */}
      <div className="sr-only">
        プランの検索を実行中です。しばらくお待ちください。
        {showCancel && onCancel && 'キャンセルボタンで検索を中止できます。'}
      </div>
    </div>
  );
};

export default LoadingState;